import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { page } from '../../store/auth/actions';
import { navigationUrl } from '../../store/analytic/actions';
import {
  confirmNegotiation,
  getDebtDetails,
} from '../../store/negotiation/actions';
import BreadCrumb from '../../components/BreadCrumb';
import { IconBack, DebtsRoot, Header } from './styles';
import { getContractsFromDealOrDebt } from '../../utils/getContractsToShow';
import ModalContracts from '../../components/Modal/Contracts';
import Loading from '../../components/Loading';
import DebtInfos from '../../components/DebtInfos';
import PaymentCard from '../../components/PaymentCard';
import DebtButton from '../../components/DebtButton';
import { HandlerNextAction } from './AbstractNegotiationService';
import api from '../../services/api';

type NegotiationProps = {
  contract: GQL.IDebt;
  negotiation: GQL.IOption;
  agreement: GQL.IDeal;
};

const AbstractNegotiation: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<NegotiationProps>();
  const confirmationInfos = useSelector(
    (state: ApplicationState) => state.negotiation.confirmationInfos
  );
  const debtDetails = useSelector(
    (state: ApplicationState) => state.negotiation.debtDetailsInfos
  );
  const contract: GQL.IDebt = location.state && location.state.contract;
  const hasManyContracts = contract?.contracts.length > 1;
  const negotiationOption: GQL.IOption =
    location.state && location.state.negotiation;
  const { innerWidth } = useWindowDimensions();
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [goNext, setGoNext] = useState<boolean>(false);
  const [confirmationSteps, setConfirmationSteps] = useState<
    'flow-one' | 'flow-two' | 'final-flow' | 'no-flow'
  >('no-flow');

  const contractsToShow = useMemo(() => getContractsFromDealOrDebt(contract), [
    contract,
  ]);

  const hasExecutedFinalFlow = useRef(false);

  const setNegotiationSteps = useCallback(() => {
    if (!confirmationInfos) return setConfirmationSteps('no-flow');
    const firstConfirmationFlow =
      confirmationInfos.enderecos &&
      !confirmationInfos.emails &&
      !confirmationInfos.telefones;

    const secondConfirmationFlow =
      confirmationInfos.enderecos &&
      confirmationInfos.emails &&
      confirmationInfos.telefones;

    if (firstConfirmationFlow) setConfirmationSteps('flow-one');
    else if (secondConfirmationFlow) setConfirmationSteps('flow-two');
    else setConfirmationSteps('final-flow');
  }, [confirmationInfos]);

  useEffect(() => {
    setNegotiationSteps();
  }, [confirmationInfos, setNegotiationSteps]);

  //FINAL-FLOW
  const execFinalFlow = useCallback(() => {
    if (hasExecutedFinalFlow.current || !goNext) return;
    hasExecutedFinalFlow.current = true;
    HandlerNextAction({
      negotiationOption: negotiationOption,
      contract: contract,
      databaseId: contract.databaseId,
      history,
      dealInfos: {
        contrato: contract.contracts,
        opcaoID: `${negotiationOption.optionId}`,
        valorEntrada: negotiationOption.firstPortionValue || 0,
        valorDemais: negotiationOption.othersPortionsValue || 0,
      },
    });
  }, [goNext, negotiationOption, contract, history]);

  //FLOW-ONE
  const execFlowOne = useCallback(() => {
    if (confirmationSteps === 'flow-one' && goNext) {
      history.push({
        pathname: '/enderecos',
        state: {
          addresses: confirmationInfos?.enderecos,
          questions: confirmationInfos?.questionamentos,
          contract,
          negotiationOption,
        },
      });
    }
  }, [
    confirmationSteps,
    negotiationOption,
    goNext,
    history,
    contract,
    confirmationInfos,
  ]);

  //FLOW-TWO
  const execFlowTwo = useCallback(() => {
    if (confirmationSteps === 'flow-two' && goNext) {
      history.push({
        pathname: '/confirmação-de-contatos',
        state: {
          addresses: confirmationInfos?.enderecos,
          phones: confirmationInfos?.telefones,
          emails: confirmationInfos?.emails,
          contract,
          negotiationOption,
        },
      });
    }
  }, [
    confirmationInfos,
    history,
    confirmationSteps,
    goNext,
    contract,
    negotiationOption,
  ]);

  //This effect handle with confirmation failure
  useEffect(() => {
    dispatch(confirmNegotiation.failure(null));
  }, [dispatch]);

  const getConfirmatedInfoIsRequired = useCallback(async () => {
    if (!contract) return;
    const { creditorId, databaseId } = contract;
    const dataResp = await api.getDataConfirmationIsRequired({
      creditorId: creditorId,
      credorDatabaseId: databaseId,
    });

    if (!dataResp?.confirmaDadosCadastrais) {
      setConfirmationSteps('final-flow');
    }
  }, [contract]);

  useEffect(() => {
    getConfirmatedInfoIsRequired();
  }, [contract, getConfirmatedInfoIsRequired]);

  //this effect prevent the user navigate where handle his debts while contracts and negotiationOptions is not loaded
  useEffect(() => {
    if (contract && negotiationOption) {
      dispatch(page('contrato'));
      dispatch(
        navigationUrl.request({
          action: '/confirmarNegociacao',
          creditor: contract.creditorName,
          dealValue:
            (negotiationOption.firstPortionValue ?? 0) +
            (negotiationOption.plan ? negotiationOption.plan - 1 : 1 - 1) *
              (negotiationOption.othersPortionsValue ?? 0),
          dealType: contract.proposalType === null ? 6 : contract.proposalType,
          delay: contract.longerDelay,
          plan: negotiationOption.plan,
        })
      );
    } else {
      history.push('/contrato');
    }
  }, [dispatch, contract, history, negotiationOption]);

  //this effect define which flow will start
  useEffect(() => {
    if (confirmationSteps === 'final-flow') return execFinalFlow();
    if (confirmationSteps === 'flow-one') return execFlowOne();
    if (confirmationSteps === 'flow-two') return execFlowTwo();
  }, [execFlowOne, execFlowTwo, execFinalFlow, confirmationSteps]);

  const handlerOptions = useCallback(() => {
    setLoading(true);
    setGoNext(true);
    dispatch(
      confirmNegotiation.request({
        databaseId: contract.databaseId,
        options: {
          contrato: contract.contracts,
          opcaoID: `${negotiationOption.optionId}`,
        },
      })
    );
  }, [contract, negotiationOption, dispatch]);

  const formatType = (type: string) => {
    if (type === 'Quitacao') return 'Quitação';
    else if (type === 'Renegociacao') return 'Renegociação';
    else {
      return 'Atualização';
    }
  };

  useEffect(() => {
    const fetchDebtDetails = async () => {
      if (contract && negotiationOption) {
        await dispatch(
          getDebtDetails.request({
            databaseId: contract.databaseId,
            options: {
              contrato: contract.contracts[0],
              opcaoID: `${negotiationOption.optionId}`,
            },
          })
        );
      }
    };

    fetchDebtDetails();
  }, [contract, negotiationOption, dispatch]);

  if (!contract || !negotiationOption) return <></>;
  return (
    <DebtsRoot>
      <div className='content'>
        <Header>
          <div className='sub-section-header'>
            <div className='title-section-header'>
              <IconBack onClick={() => history.goBack()} />
              <h2 className='title'>Resumo</h2>
            </div>
            <BreadCrumb
              previouPages={[
                { name: 'Débitos e Acordos', route: '/contrato' },
                { name: 'Meu Débito', route: '/negociacao' },
              ]}
              currentPage='Resumo'
              showBreadCrumbOnMinWidth={'960px'}
            />
          </div>
        </Header>
        <DebtInfos
          creditor={contract.creditorName}
          currentDealValue={contract.updatedValue}
          multipleContracts={hasManyContracts}
          dueDate={negotiationOption.formatedDueDate || ''}
          contractsToShow={contractsToShow[0]}
          debtDetails={debtDetails?.textoConfirmacao || ''}
          openModal={setOpen}
        />

        <div className='section-cards'>
          <h2 className='sub-title'>Opção de pagamento selecionada</h2>
          <PaymentCard
            plan={negotiationOption.plan || 0}
            firstPortion={negotiationOption.firstPortionValue || 0}
            othersPortions={negotiationOption.othersPortionsValue || 0}
            type={formatType(
              negotiationOption.negotiationTypeDescription || ''
            )}
            paymentMethod={negotiationOption.paymentMethod || ''}
            hiddeArrow={true}
          />
        </div>

        <div className='footer'>
          <DebtButton
            color='secondary'
            text='voltar'
            onClick={() => history.goBack()}
          />
          <DebtButton
            color='primary'
            text={innerWidth < 960 ? 'confirmar' : 'confirmar e pagar'}
            onClick={handlerOptions}
            loading={loading}
            disabled={loading}
            loadingComponent={<Loading />}
          />
        </div>
      </div>
      {open && (
        <ModalContracts
          handlerClose={() => setOpen(false)}
          contracts={contractsToShow}
        />
      )}
    </DebtsRoot>
  );
};

export default AbstractNegotiation;
